import axios from 'axios'
import {Formik} from 'formik'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import FormCheckbox from 'src/cms/helpers/components/forms/FormCheckbox'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import FormTinyMce from 'src/cms/helpers/components/forms/FormTinyMce'
import checkBackendSetting from 'src/app/modules/common/checkBackendSetting'
import * as Yup from 'yup'

const API_URL = window.__RUNTIME_CONFIG__.REACT_APP_API_URL
export const PRODUCT_LOAN_ELIGIBILITY_URL = `${API_URL}/loan-eligibility-setting`

export interface ProductLoanEligibilityModel {
  isEnabled: boolean
  interestRate: number | string | null
  tenureMin: number | string | null
  tenureMax: number | string | null
  maxFinancingPercent: number | string | null
  minAge: number | string | null
  maxAge: number | string | null
  loanTypePopup: string | null
  loanTypePopupNp: string | null
  // Siddhartha formula (backend setting loanEligibilitySblFormula)
  collateralType: string | null
  dbrPercent: number | string | null
  marketValueDeductionPercent: number | string | null
  electricLtvPercent: number | string | null
  purposeRules: PurposeRule[]
}

interface PurposeRule {
  id: string
  title?: string
  dbrPercent: number | string | null
  ltvPercent: number | string | null
  ltvOutsideValleyPercent: number | string | null
}

const EMPTY: ProductLoanEligibilityModel = {
  isEnabled: false,
  interestRate: '',
  tenureMin: '',
  tenureMax: '',
  maxFinancingPercent: '',
  minAge: '',
  maxAge: '',
  loanTypePopup: '',
  loanTypePopupNp: '',
  collateralType: '',
  dbrPercent: '',
  marketValueDeductionPercent: '',
  electricLtvPercent: '',
  purposeRules: [],
}

const number = (label: string) =>
  Yup.number().typeError(`${label} must be a number`).min(0, `${label} cannot be negative`).nullable()
const requiredWhenEnabled = (schema: any, label: string) =>
  schema.when('isEnabled', {is: true, then: (s: any) => s.required(`${label} is required`)})

const validationSchema = Yup.object().shape({
  interestRate: requiredWhenEnabled(number('Interest rate').max(100), 'Interest rate'),
  tenureMin: requiredWhenEnabled(number('Minimum tenure').integer().min(1), 'Minimum tenure'),
  tenureMax: requiredWhenEnabled(
    number('Maximum tenure')
      .integer()
      .min(Yup.ref('tenureMin'), 'Maximum tenure must not be less than the minimum'),
    'Maximum tenure'
  ),
  maxFinancingPercent: number('Max financing').max(100),
  minAge: number('Minimum age').integer(),
  maxAge: number('Maximum age')
    .integer()
    .min(Yup.ref('minAge'), 'Maximum age must not be less than the minimum'),
  dbrPercent: number('DBR').max(100),
  marketValueDeductionPercent: number('Market value deduction').max(100),
  electricLtvPercent: number('Electric vehicle LTV').max(100),
})

// '' from an emptied input -> null for the API
const toPayload = (v: ProductLoanEligibilityModel) => {
  const n = (x: any) => (x === '' || x === null || x === undefined ? null : Number(x))
  return {
    isEnabled: !!v.isEnabled,
    interestRate: n(v.interestRate),
    tenureMin: n(v.tenureMin),
    tenureMax: n(v.tenureMax),
    maxFinancingPercent: n(v.maxFinancingPercent),
    minAge: n(v.minAge),
    maxAge: n(v.maxAge),
    loanTypePopup: v.loanTypePopup || null,
    loanTypePopupNp: v.loanTypePopupNp || null,
    collateralType: v.collateralType || null,
    dbrPercent: n(v.dbrPercent),
    marketValueDeductionPercent: n(v.marketValueDeductionPercent),
    electricLtvPercent: n(v.electricLtvPercent),
    purposeRules: (v.purposeRules || []).map((r) => ({
      id: r.id,
      dbrPercent: n(r.dbrPercent),
      ltvPercent: n(r.ltvPercent),
      ltvOutsideValleyPercent: n(r.ltvOutsideValleyPercent),
    })),
  }
}

const RULE_COLUMNS: [keyof PurposeRule, string][] = [
  ['dbrPercent', 'DBR %'],
  ['ltvPercent', 'LTV %'],
  ['ltvOutsideValleyPercent', 'LTV % outside valley'],
]

/**
 * "Loan Eligibility" tab of the product form: the parameters the public loan-eligibility check uses
 * (legacy tbl_product loan columns). Saved separately from the product (its own endpoint), so it has
 * its own Formik and Save button and is shown only for an existing product.
 */
export function ProductLoanEligibilityComponent({productId}: {productId: string}) {
  const [initial, setInitial] = useState<ProductLoanEligibilityModel>(EMPTY)
  const [saving, setSaving] = useState(false)
  const sblFormula = checkBackendSetting('loanEligibilitySblFormula')

  useEffect(() => {
    axios
      .get(`${PRODUCT_LOAN_ELIGIBILITY_URL}/${productId}`)
      .then((res) => {
        const d = res?.data?.data || {}
        setInitial(
          Object.fromEntries(
            Object.keys(EMPTY).map((k) => [k, d[k] ?? (EMPTY as any)[k]])
          ) as ProductLoanEligibilityModel
        )
      })
      .catch(() => {})
  }, [productId])

  return (
    <Formik
      enableReinitialize
      initialValues={initial}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setSaving(true)
        axios
          .put(`${PRODUCT_LOAN_ELIGIBILITY_URL}/${productId}`, toPayload(values))
          .then(() => toast.success('Loan eligibility settings saved successfully'))
          .catch((err) => toast.error(err?.response?.data?.message || 'Could not save loan eligibility settings'))
          .finally(() => setSaving(false))
      }}
    >
      {({errors, touched, handleChange, handleSubmit, values, setFieldValue}) => (
        <div className='card-body border-top pt-5' data-cy='loan-eligibility-tab'>
          <FormCheckbox
            name='isEnabled'
            label='Show in Loan Eligibility Check'
            checkBoxText='Enabled'
            checked={!!values.isEnabled}
            onChange={handleChange}
            errors={errors}
            touched={touched}
          />
          {[
            ['interestRate', 'Interest Rate (% per annum)', values.isEnabled],
            ['tenureMin', 'Minimum Tenure (years)', values.isEnabled],
            ['tenureMax', 'Maximum Tenure (years)', values.isEnabled],
            ['maxFinancingPercent', 'Max Financing (% of collateral)', false],
            ['minAge', 'Minimum Age (years)', false],
            ['maxAge', 'Maximum Age (years)', false],
          ].map(([name, label, required]) => (
            <FormTextBox
              key={name as string}
              type='number'
              name={name}
              label={label as string}
              placeholder={label}
              labelClassName='col-lg-4'
              containerClassName='col-lg-6'
              onChange={handleChange}
              errors={errors}
              touched={touched}
              required={!!required}
            />
          ))}
          {sblFormula && (
            <>
              <div className='row mb-6'>
                <label htmlFor='collateralType' className='mb-1 fw-bolder fs-6 col-lg-4'>
                  Collateral (DBR / LTV)
                </label>
                <div className='col-lg-6'>
                  <select
                    id='collateralType'
                    name='collateralType'
                    className='form-select form-select-sm'
                    value={values.collateralType || ''}
                    onChange={handleChange}
                  >
                    <option value=''>None: gross income only</option>
                    <option value='property'>Property (home loan)</option>
                    <option value='vehicle'>Vehicle (auto loan)</option>
                  </select>
                </div>
              </div>
              {[
                ['dbrPercent', 'DBR (% of gross monthly income)'],
                ['marketValueDeductionPercent', 'Market Value Deduction (%, property)'],
                ['electricLtvPercent', 'LTV for Electric Vehicles (%)'],
              ].map(([name, label]) => (
                <FormTextBox
                  key={name}
                  type='number'
                  name={name}
                  label={label}
                  placeholder={label}
                  labelClassName='col-lg-4'
                  containerClassName='col-lg-6'
                  onChange={handleChange}
                  errors={errors}
                  touched={touched}
                />
              ))}
              {values.purposeRules.length > 0 && (
                <div className='row mb-6'>
                  <label className='mb-1 fw-bolder fs-6 col-lg-4'>
                    Per Purpose (empty = product value; LTV falls back to Max Financing)
                  </label>
                  <div className='col-lg-6'>
                    <table className='table table-sm align-middle'>
                      <thead>
                        <tr className='fw-bolder'>
                          <th>Purpose</th>
                          {RULE_COLUMNS.map(([, label]) => (
                            <th key={label}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {values.purposeRules.map((rule, i) => (
                          <tr key={rule.id}>
                            <td>{rule.title}</td>
                            {RULE_COLUMNS.map(([key, label]) => (
                              <td key={key}>
                                <input
                                  type='number'
                                  min={0}
                                  max={100}
                                  aria-label={`${rule.title} ${label}`}
                                  className='form-control form-control-sm'
                                  value={(rule[key] as any) ?? ''}
                                  onChange={(e) => setFieldValue(`purposeRules.${i}.${key}`, e.target.value)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          <div className='row'>
            <div className='col-md-6 col-xs-12'>
              <FormTinyMce
                containerClassName='col-md-12'
                label='Loan Type Popup (EN)'
                name='loanTypePopup'
                initialValue={initial.loanTypePopup || ''}
                handleChange={handleChange}
              />
            </div>
            <div className='col-md-6 col-xs-12'>
              <FormTinyMce
                containerClassName='col-md-12'
                label='Loan Type Popup (NP)'
                name='loanTypePopupNp'
                initialValue={initial.loanTypePopupNp || ''}
                handleChange={handleChange}
              />
            </div>
          </div>
          <div className='d-flex justify-content-end'>
            <button
              type='button'
              className='btn btn-primary btn-sm'
              disabled={saving}
              onClick={() => handleSubmit()}
              data-cy='loan-eligibility-save-button'
            >
              Save Loan Eligibility
            </button>
          </div>
        </div>
      )}
    </Formik>
  )
}
